<template>
    <div class="membership-editor">
        <v-dialog
            v-model="dialog"
            max-width="560"
            scrollable
            :persistent="busy || confirmDialog"
            :aria-labelledby="`${formId}-title`"
        >
            <template v-slot:activator="{ on, attrs }">
                <v-btn
                    class="branch-trigger font-weight-bold"
                    color="#f0784c"
                    text
                    small
                    :disabled="loggedInUser || busy || !accountEmail"
                    v-bind="attrs"
                    v-on="on"
                    >Modify</v-btn
                >
            </template>
            <v-card class="branch-dialog" dark elevation="0" :aria-busy="busy ? 'true' : 'false'">
                <v-card-title class="branch-dialog__header">
                    <p class="eyebrow">Organization membership</p>
                    <h2 :id="`${formId}-title`">Modify user account</h2>
                    <p>Choose the right level of access for this organization.</p>
                </v-card-title>
                <v-card-text class="branch-dialog__body">
                    <section class="profile-summary" aria-label="Member profile">
                        <span class="profile-avatar" aria-hidden="true">{{ avatarInitial }}</span>
                        <div class="profile-details">
                            <h3>{{ accountEmail || 'Email unavailable' }}</h3>
                            <p>{{ organizationName }}</p>
                            <span class="current-role">Current role: {{ currentRole }}</span>
                        </div>
                    </section>

                    <fieldset class="role-picker" :disabled="busy || loggedInUser || confirmDialog">
                        <legend>Organization role</legend>
                        <p :id="`${formId}-role-help`" class="section-help"
                            >Select one role. Changes apply here only.</p
                        >
                        <div class="role-options">
                            <label class="role-choice" :class="{ 'role-choice--selected': !adminRole }">
                                <input
                                    v-model="adminRole"
                                    type="radio"
                                    :name="`${formId}-role`"
                                    :value="false"
                                    :aria-describedby="`${formId}-role-help ${formId}-member-help`"
                                />
                                <span>
                                    <strong>Member</strong>
                                    <span :id="`${formId}-member-help`"
                                        >Access organization services without managing members.</span
                                    >
                                </span>
                            </label>
                            <label class="role-choice" :class="{ 'role-choice--selected': adminRole }">
                                <input
                                    v-model="adminRole"
                                    type="radio"
                                    :name="`${formId}-role`"
                                    :value="true"
                                    :aria-describedby="`${formId}-role-help ${formId}-admin-help`"
                                />
                                <span>
                                    <strong>Administrator</strong>
                                    <span :id="`${formId}-admin-help`"
                                        >Manage organization members, roles, and settings.</span
                                    >
                                </span>
                            </label>
                        </div>
                    </fieldset>

                    <div class="change-summary" role="status" aria-live="polite" aria-atomic="true">
                        <strong>{{ roleChanged ? 'Pending role change' : 'No changes to save' }}</strong>
                        <p v-if="roleChanged">{{ currentRole }} → {{ selectedRole }} in {{ organizationName }}.</p>
                        <p v-else>The current {{ currentRole.toLowerCase() }} role will stay the same.</p>
                    </div>
                    <p v-if="saveError" class="form-error" role="alert">{{ saveError }}</p>
                    <p v-if="loggedInUser" class="section-help">Your own role cannot be modified here.</p>

                    <section class="removal-section" :aria-labelledby="`${formId}-removal-heading`">
                        <div>
                            <h3 :id="`${formId}-removal-heading`">Remove membership</h3>
                            <p>Remove access to this organization, not the user's account.</p>
                        </div>
                        <v-btn
                            class="branch-danger open-removal"
                            text
                            :disabled="busy || loggedInUser || confirmDialog || !accountEmail"
                            @click="openRemoval"
                            >Remove member</v-btn
                        >
                    </section>
                </v-card-text>
                <v-card-actions class="branch-dialog__actions">
                    <v-btn
                        class="branch-secondary cancel-edit"
                        text
                        :disabled="busy || confirmDialog"
                        @click="closeEditor"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        class="branch-primary save-role"
                        color="#db5120"
                        elevation="0"
                        :loading="saving"
                        :disabled="!canSave"
                        @click="updateUser"
                    >
                        Save role
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            v-model="confirmDialog"
            max-width="560"
            scrollable
            :persistent="busy"
            :aria-labelledby="`${formId}-confirm-title`"
        >
            <v-card class="branch-dialog" dark elevation="0" :aria-busy="busy ? 'true' : 'false'">
                <v-card-title class="branch-dialog__header">
                    <p class="eyebrow">Confirm membership removal</p>
                    <h2 :id="`${formId}-confirm-title`">Remove this member?</h2>
                </v-card-title>
                <v-card-text class="branch-dialog__body">
                    <p class="removal-explanation">
                        <strong>{{ accountEmail }}</strong> will lose access to <strong>{{ organizationName }}</strong
                        >. Their account will not be deleted. Any unsaved role change will not be applied.
                    </p>
                    <p v-if="removalError" class="form-error" role="alert">{{ removalError }}</p>
                </v-card-text>
                <v-card-actions class="branch-dialog__actions">
                    <v-btn class="branch-secondary cancel-removal" text :disabled="busy" @click="cancelRemoval">
                        Cancel
                    </v-btn>
                    <v-btn
                        class="branch-danger confirm-removal"
                        outlined
                        elevation="0"
                        :loading="removing"
                        :disabled="!canRemove"
                        @click="deleteUser"
                        >Remove membership</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import gql from 'graphql-tag'

export default {
    name: 'UpdateUser',
    props: {
        currentLevel: {
            type: Object,
            required: true
        },
        currentAccount: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            dialog: false,
            confirmDialog: false,
            adminRole: false,
            saving: false,
            removing: false,
            saveError: '',
            removalError: '',
            formVersion: 0
        }
    },
    computed: {
        formId() {
            return `membership-${this._uid}`
        },
        loggedInUser() {
            return this.$auth.user.id === this.currentAccount.accountId
        },
        accountEmail() {
            return this.currentAccount.account ? this.currentAccount.account.email || '' : ''
        },
        organizationName() {
            return this.currentLevel.baseAccountName || this.currentLevel.organizationName || 'this organization'
        },
        avatarInitial() {
            return this.accountEmail.charAt(0).toUpperCase() || '?'
        },
        currentRole() {
            return this.currentAccount.admin === true ? 'Administrator' : 'Member'
        },
        selectedRole() {
            return this.adminRole ? 'Administrator' : 'Member'
        },
        roleChanged() {
            return this.adminRole !== (this.currentAccount.admin === true)
        },
        busy() {
            return this.saving || this.removing
        },
        canSave() {
            return this.dialog && this.roleChanged && !this.confirmDialog && this.canModify && !this.busy
        },
        canModify() {
            return (
                !this.loggedInUser &&
                !!this.accountEmail &&
                !!this.currentAccount.accountId &&
                !!this.currentLevel.baseAccountId
            )
        },
        canRemove() {
            return this.dialog && this.confirmDialog && this.canModify && !this.busy
        }
    },
    watch: {
        dialog(open) {
            if (open) {
                this.resetForm()
                if (this.loggedInUser) this.dialog = false
            } else {
                this.confirmDialog = false
            }
        },
        confirmDialog(open) {
            if (!open) {
                this.removalError = ''
            }
        },
        currentAccount: {
            immediate: true,
            deep: true,
            handler() {
                this.resetForm()
                if (this.loggedInUser) this.dialog = false
            }
        },
        'currentLevel.baseAccountId'() {
            this.resetForm()
        }
    },
    methods: {
        resetForm() {
            this.formVersion += 1
            this.adminRole = this.currentAccount.admin === true
            this.confirmDialog = false
            this.saveError = ''
            this.removalError = ''
        },
        closeEditor() {
            if (!this.busy && !this.confirmDialog) this.dialog = false
        },
        openRemoval() {
            if (!this.dialog || this.busy || !this.canModify) return
            this.removalError = ''
            this.confirmDialog = true
        },
        cancelRemoval() {
            if (this.busy) return
            this.confirmDialog = false
            this.removalError = ''
        },
        failureMessage(action, detail) {
            const reason = typeof detail === 'string' && detail.trim() ? ` ${detail}` : ''
            return `Could not ${action}.${reason} Please try again.`
        },
        async updateUser() {
            if (!this.canSave) return
            this.saving = true
            this.saveError = ''
            const version = this.formVersion
            const email = this.accountEmail
            try {
                const response = await this.$apollo.mutate({
                    mutation: gql`
                        mutation UpdateUserOfOrganization($admin: Boolean!, $userId: String!, $orgId: String!) {
                            updateUserOfOrganization(admin: $admin, userId: $userId, orgId: $orgId) {
                                complete
                                error
                                success
                                uid
                            }
                        }
                    `,
                    variables: {
                        admin: this.adminRole,
                        orgId: this.currentLevel.baseAccountId,
                        userId: this.currentAccount.accountId
                    }
                })
                if (version !== this.formVersion) return
                const result = response && response.data && response.data.updateUserOfOrganization
                if (result && result.success === true) {
                    this.confirmDialog = false
                    this.dialog = false
                    this.$emit('updated', `'${email}' successfully updated`)
                } else {
                    this.saveError = this.failureMessage('save the role', result && result.error)
                }
            } catch (error) {
                if (version === this.formVersion) this.saveError = this.failureMessage('save the role', error.message)
            } finally {
                this.saving = false
            }
        },
        async deleteUser() {
            if (!this.canRemove) return
            this.removing = true
            this.removalError = ''
            const version = this.formVersion
            const email = this.accountEmail
            try {
                const response = await this.$apollo.mutate({
                    mutation: gql`
                        mutation RemoveUserFromOrganization($orgId: String!, $email: String, $userId: String) {
                            removeUserFromOrganization(orgId: $orgId, email: $email, userId: $userId) {
                                complete
                                error
                                success
                                uid
                            }
                        }
                    `,
                    variables: {
                        orgId: this.currentLevel.baseAccountId,
                        email: this.currentAccount.account.email,
                        userId: this.currentAccount.accountId
                    }
                })
                if (version !== this.formVersion) return
                const result = response && response.data && response.data.removeUserFromOrganization
                if (result && result.success === true) {
                    this.confirmDialog = false
                    this.dialog = false
                    this.$emit('updated', `'${email}' removed from the organization`)
                } else {
                    this.removalError = this.failureMessage('remove the membership', result && result.error)
                }
            } catch (error) {
                if (version === this.formVersion) {
                    this.removalError = this.failureMessage('remove the membership', error.message)
                }
            } finally {
                this.removing = false
            }
        }
    }
}
</script>

<style scoped>
.branch-trigger,
.branch-dialog {
    font-family: 'Inter Tight', 'Inter', sans-serif;
    font-size: 12px;
}
.branch-trigger {
    padding: 0 8px;
    border-radius: 6px;
    letter-spacing: 0;
    text-transform: none;
}
.branch-dialog.v-card {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 48px);
    border: 1px solid #454953;
    border-radius: 12px;
    background: #282b33;
    color: #f5f5f7;
}
.branch-dialog__header {
    flex: 0 0 auto;
    display: block;
    padding: 20px 24px;
    border-bottom: 1px solid #454953;
}
.branch-dialog h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    line-height: 1.25;
    letter-spacing: -0.02em;
    overflow-wrap: anywhere;
}
.branch-dialog__header p {
    margin: 8px 0 0;
    color: #bfc3ce;
    font-size: 12px;
    line-height: 1.5;
}
.branch-dialog__header .eyebrow {
    margin: 0 0 8px;
    color: #f0784c;
    font-weight: 600;
}
.branch-dialog .branch-dialog__body {
    min-height: 0;
    padding: 20px 24px;
    overflow-y: auto;
    color: #d4d6dd;
    font-size: 12px;
    line-height: 1.6;
}
.profile-summary {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 20px;
    padding: 16px;
    border: 1px solid #454953;
    border-radius: 12px;
    background: #22252d;
}
.profile-avatar {
    display: grid;
    flex: 0 0 40px;
    place-items: center;
    height: 40px;
    border-radius: 12px;
    background: #3b3030;
    color: #f0784c;
    font-size: 18px;
    font-weight: 600;
}
.profile-details {
    min-width: 0;
    overflow-wrap: anywhere;
}
.profile-details h3 {
    margin: 0;
    color: #f5f5f7;
    font-size: 14px;
    line-height: 1.5;
}
.profile-details p {
    margin: 4px 0 8px;
}
.current-role {
    color: #bfc3ce;
}
.role-picker {
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
}
.role-picker legend {
    margin-bottom: 4px;
    color: #f5f5f7;
    font-size: 14px;
    font-weight: 600;
}
.branch-dialog .section-help {
    margin: 0 0 12px;
    color: #bfc3ce;
}
.role-options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}
.role-choice {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px;
    border: 1px solid #686d79;
    border-radius: 12px;
    background: #22252d;
    cursor: pointer;
}
.role-choice--selected {
    border-color: #f0784c;
    background: #353036;
}
.role-choice input {
    flex: 0 0 16px;
    width: 16px;
    height: 16px;
    margin-top: 3px;
    accent-color: #f0784c;
}
.role-choice strong {
    display: block;
    margin-bottom: 4px;
    color: #f5f5f7;
    font-size: 14px;
}
.role-choice span span {
    display: block;
    color: #bfc3ce;
}
.role-choice:focus-within {
    outline: 2px solid #f0784c;
    outline-offset: 3px;
}
.role-picker:disabled .role-choice {
    opacity: 0.6;
    cursor: not-allowed;
}
.change-summary {
    margin-top: 16px;
    padding: 12px 14px;
    border-left: 3px solid #f0784c;
    border-radius: 0 8px 8px 0;
    background: #22252d;
    overflow-wrap: anywhere;
}
.branch-dialog .change-summary p {
    margin: 4px 0 0;
}
.branch-dialog .form-error {
    margin: 12px 0 0;
    padding: 12px;
    border: 1px solid #a45c58;
    border-radius: 8px;
    color: #ffb4ac;
    overflow-wrap: anywhere;
}
.removal-section {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #454953;
}
.removal-section h3 {
    margin: 0 0 4px;
    color: #e0e2e8;
    font-size: 12px;
}
.branch-dialog .removal-section p {
    margin: 0;
    color: #bfc3ce;
}
.removal-section .v-btn {
    flex: 0 0 auto;
}
.removal-explanation {
    overflow-wrap: anywhere;
}
.branch-dialog__actions {
    flex: 0 0 auto;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
    padding: 16px 24px;
    border-top: 1px solid #454953;
    background: #22252d;
}
.branch-dialog .v-btn {
    max-width: 100%;
    height: auto;
    min-height: 36px;
    margin: 0;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 12px;
    letter-spacing: 0;
    text-transform: none;
}
.branch-dialog ::v-deep .v-btn__content {
    white-space: normal;
    overflow-wrap: anywhere;
}
.branch-dialog .branch-secondary {
    border-color: #686d79;
    color: #e0e2e8;
}
.branch-dialog .branch-primary {
    color: #fff;
}
.branch-dialog .branch-danger {
    border-color: #a45c58;
    color: #ffaaa3;
}
.branch-dialog ::v-deep .v-input,
.branch-dialog ::v-deep .v-label,
.branch-dialog ::v-deep input {
    font-family: inherit;
    font-size: 12px;
}
.branch-dialog ::v-deep .v-text-field--outlined .v-input__slot {
    background: #22252d;
    border-radius: 8px;
}
.branch-dialog ::v-deep .v-text-field--outlined:not(.v-input--is-focused):not(.error--text) fieldset {
    border-color: #686d79;
}
.branch-trigger:focus-visible,
.branch-dialog ::v-deep .v-btn:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 3px;
}
@media (max-width: 600px) {
    .branch-dialog__header,
    .branch-dialog .branch-dialog__body,
    .branch-dialog__actions {
        padding: 16px;
    }
    .branch-dialog h2 {
        font-size: 20px;
    }
    .role-options {
        grid-template-columns: minmax(0, 1fr);
    }
    .removal-section {
        align-items: flex-start;
        flex-direction: column;
    }
}
</style>
